import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Grid, Button } from '@mui/material';
import Loader from '../../../../components/loader';
import { userTypeGetById } from '../userType.service';
import { sanitizeHtml } from '../../../../../helper/sanitizeHtml';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../../styles/detailStyles';

const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

function DetailField({ label, value, fullWidth = false, minHeight, html = false }) {
  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>
          {label}
        </Typography>
        {html ? (
          <Box
            sx={{
              ...detailValueSx,
              '& p': { my: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
          />
        ) : (
          <Typography sx={detailValueSx}>
            {value || '-'}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

const UserTypeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userTypeData, setUserTypeData] = useState({});
  const [loading, setLoading] = useState(false);

  const getUserTypeData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userTypeGetById(id);
      setUserTypeData(data?.data || {});
    } catch (error) {
      console.error('Error fetching user type by ID:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getUserTypeData();
  }, [getUserTypeData]);

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/userType')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': {
                fontSize: 18,
              },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            User Persona Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          <Button
            onClick={() => navigate(`/addUserType/${id}?type=edit`)}
            startIcon={<span className="material-symbols-rounded">edit</span>}
            sx={{
              minWidth: { xs: '100%', sm: 86 },
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#DFE5F6',
              color: '#294190',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#DFE5F6',
              },
              '& .material-symbols-rounded': {
                fontSize: 18,
              },
            }}
          >
            Edit
          </Button>
        </Grid>
      </Grid>

      <Box sx={pageCardSx}>
        <Typography sx={detailSectionTitleSx}>
          Persona Details
        </Typography>

        <Grid container spacing={2.25}>
          <DetailField label="User Persona" value={userTypeData?.name} />
          <DetailField label="Short Name" value={userTypeData?.shortName} />
          <DetailField label="Country" value={userTypeData?.countryNames?.join(', ')} />
          <DetailField label="Status" value={userTypeData?.status} />
          <DetailField label="Redirection URL" value={userTypeData?.appUrl} />

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                ...detailFieldSx,
                minHeight: 120,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {userTypeData?.iconUrl ? (
                <Box
                  component="img"
                  src={`${userTypeData.iconUrl}?${sasToken}`}
                  alt="User Persona Icon"
                  sx={{ maxWidth: '100%', maxHeight: 88, objectFit: 'contain', borderRadius: '12px' }}
                  onError={(event) => {
                    event.target.onerror = null;
                    event.target.style.display = 'none';
                  }}
                />
              ) : (
                <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#6C6A86' }}>
                  No icon uploaded
                </Typography>
              )}
            </Box>
          </Grid>

          <DetailField label="Description" value={userTypeData?.description} fullWidth minHeight={120} html />
        </Grid>
      </Box>

      {loading && <Loader />}
    </Box>
  );
};

export default UserTypeView;
